import {Box, Typography, Grid, Paper, TextField} from "@mui/material";
import React, {useEffect, useState, DragEvent} from "react";
import {ColumnSettings, ColumnConfig} from "@/services/Template/TemplateService";
import CustomNoRowsGrid from "@/components/data-grid/CustomNoRowsGrid";
import {useColumnLabel} from "../column-settings/utils";
import {useTranslation} from "react-i18next";

type RightProps = {
  columnSettings: ColumnSettings | null;
  updateColumnSettings: (columnSettings: ColumnSettings) => void;
};

const Right: React.FC<RightProps> = ({
                                       columnSettings,
                                       updateColumnSettings,
                                     }) => {
  const [columns, setColumns] = useState<ColumnConfig[]>([]);
  const [draggedItem, setDraggedItem] = useState<ColumnConfig | null>(null);
  const {t} = useTranslation();
  const {getColumnLabel} = useColumnLabel();
  const [dropIndicator, setDropIndicator] = useState<{
    key: string;
    position: "top" | "bottom";
  } | null>(null);

  useEffect(() => {
    if (columnSettings?.template_field) {
      const sortedColumns = [...columnSettings.template_field].sort(
        (a, b) => a.sort - b.sort,
      );
      setColumns(sortedColumns);
    }
  }, [columnSettings]);

  const handleDragStart = (
    e: DragEvent<HTMLDivElement>,
    item: ColumnConfig,
  ) => {
    setDraggedItem(item);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDropIndicator(null);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>, item: ColumnConfig) => {
    e.preventDefault();
    if (!draggedItem || draggedItem.key === item.key) {
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const middleY = rect.top + rect.height / 2;
    const position = e.clientY < middleY ? "top" : "bottom";
    setDropIndicator({key: item.key, position});
  };

  const handleDragLeave = () => {
    setDropIndicator(null);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!draggedItem || !dropIndicator) {
      return;
    }

    const reorderedColumns = [...columns];
    const draggedItemIndex = reorderedColumns.findIndex(
      (c) => c.key === draggedItem.key,
    );
    if (draggedItemIndex === -1) {
      setDraggedItem(null);
      setDropIndicator(null);
      return;
    }

    const [removed] = reorderedColumns.splice(draggedItemIndex, 1);

    const newTargetIndex = reorderedColumns.findIndex(
      (c) => c.key === dropIndicator.key,
    );
    if (newTargetIndex === -1) {
      setColumns(columns);
      setDraggedItem(null);
      setDropIndicator(null);
      return;
    }

    if (dropIndicator.position === "top") {
      reorderedColumns.splice(newTargetIndex, 0, removed);
    } else {
      reorderedColumns.splice(newTargetIndex + 1, 0, removed);
    }

    const updatedColumns = reorderedColumns.map((col, index) => ({
      ...col,
      sort: index,
    }));

    setColumns(updatedColumns);
    if (columnSettings) {
      const tempConfig: Record<string, number> = {};
      updatedColumns.forEach((col) => {
        tempConfig[col.key] = col.sort;
      });
      columnSettings.template_field.forEach((col: any) => {
        col.sort = tempConfig[col.key];
      });
      updateColumnSettings({...columnSettings});
    }
    setDraggedItem(null);
    setDropIndicator(null);
  };
  const checkedCount = columns.filter(col => col.isCheck).length; // 已勾选数量
  const totalCount = columns.length; // 总字段数量


  return (
    <Grid
      sx={{
        padding: "20px",
      }}
    >
      <Grid sx={{display: "flex"}}>
        <Typography sx={{mb: 2, fontSize: '16px', color: "#333333", fontWeight: "bold"}}>
          {t("common.Selected")}
        </Typography>
        <Typography sx={{mb: 2, fontSize: '16px', color: '#999999', marginLeft: "10px", fontWeight: "normal"}}>
          ({checkedCount} / {totalCount})
        </Typography>
      </Grid>
      <Typography sx={{mb: 2, fontSize: '14px', color: '#999999'}}>
        {t("common.Click and drag to reorder")}
      </Typography>
      {checkedCount === 0 ? (
        <Grid sx={{
          position: 'absolute',
          top: '50%',
          left: '80%',
          transform: 'translate(-50%, -50%)',
          width: '100%',
          textAlign: 'center',
        }}>
          <CustomNoRowsGrid/>
        </Grid>
      ) : columns.map((column) => {

        if (!column.isCheck) {
          return null;
        }

        const isIndicatingTop =
          dropIndicator?.key === column.key &&
          dropIndicator?.position === "top";

        const isIndicatingBottom =
          dropIndicator?.key === column.key &&
          dropIndicator?.position === "bottom";

        return (
          <div
            key={column.key}
            draggable={true}
            onDragStart={(e) => handleDragStart(e, column)}
            onDragEnd={handleDragEnd}
            onDragOver={(e) => handleDragOver(e, column)}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            style={{
              cursor: "grab",
              opacity: draggedItem?.key === column.key ? 0.5 : 1,
              borderTop: isIndicatingTop ? "2px solid #1890ff" : "none",
              borderBottom: isIndicatingBottom ? "2px solid #1890ff" : "none",
              padding: "8px",
              margin: "4px 0",
              backgroundColor: "#f0f0f0",
            }}
          >
            <Grid
              sx={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <Typography>::</Typography>
              <Typography
                sx={{
                  paddingTop: "2px",
                  marginLeft: "6px",
                  color: "#3D3D3D",
                  fontSize: "14px",
                }}
              >
                {getColumnLabel(column.key)}
              </Typography>
            </Grid>
          </div>
        );
      })}
    </Grid>
  );
};

export default Right;
